import React, { useState, useEffect } from 'react';
import { ledgerAPI, categoryAPI, transactionAPI, vendorAPI } from '../services/api';
import { toast } from 'react-toastify';
import CURRENCY from '../utils/currency';
import { useTone } from '../contexts/ToneContext';
import './AddTransactionModal.css';

// Transaction type configuration with icons for visual clarity
const TRANSACTION_TYPE_KEYS = [
  { value: 'expense_paid', labelKey: 'transaction.expense_paid', icon: '−', color: '#DC3545' },
  { value: 'income_received', labelKey: 'transaction.income_received', icon: '+', color: '#28A745' },
  { value: 'self_transfer', labelKey: 'transaction.self_transfer', icon: '↔', color: '#7F8C8D' },
  { value: 'payable', labelKey: 'transaction.payable', icon: '◯', color: '#F39C12' },
  { value: 'receivable', labelKey: 'transaction.receivable', icon: '◎', color: '#3498DB' },
  { value: 'scheduled', labelKey: 'transaction.scheduled', icon: '⏰', color: '#9B59B6' },
];

const BALANCE_AFFECTING = ['income', 'expense', 'expense_paid', 'income_received'];
const LEDGER_EXEMPT = ['payable', 'receivable', 'scheduled'];
const SELF_TRANSFER_TYPE = 'self_transfer';

const isIncomeType = (type) => type === 'income' || type === 'income_received';

// Helper to check if a category is "Vendor Payment" type
const isVendorPaymentCategory = (category) => {
  if (!category) return false;
  const name = (category.name || '').toLowerCase();
  return name.includes('vendor') && (name.includes('payment') || name.includes('pay'));
};

const AddTransactionModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTone();
  const [ledgers, setLedgers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    transaction_type: 'expense_paid',
    ledger_id: '',
    to_ledger_id: '', // For self_transfer: destination ledger
    category_id: '',
    vendor_id: '',
    amount: '',
    description: '',
    transaction_date: new Date().toISOString().split('T')[0],
    commitment_date: '',
    notes: '',
  });

  useEffect(() => {
    if (isOpen) {
      fetchFormData();
    }
  }, [isOpen]);

  const fetchFormData = async () => {
    setLoading(true);
    try {
      const [ledgersRes, categoriesRes, vendorsRes] = await Promise.allSettled([
        ledgerAPI.getAll(),
        categoryAPI.getAll(),
        vendorAPI.getAll(),
      ]);

      let activeLedgers = [];
      if (ledgersRes.status === 'fulfilled') {
        const allLedgers = ledgersRes.value.data.data.ledgers || [];
        activeLedgers = allLedgers.filter(l => !l.is_archived);
        setLedgers(activeLedgers);
      }
      if (categoriesRes.status === 'fulfilled') {
        setCategories(categoriesRes.value.data.data.categories || []);
      }
      if (vendorsRes.status === 'fulfilled') {
        setVendors(vendorsRes.value.data.data.vendors || []);
      }

      // Auto-select first active ledger
      if (activeLedgers.length > 0) {
        setFormData(prev => ({ ...prev, ledger_id: activeLedgers[0].id }));
      }
    } catch (error) {
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      // If switching to ledger-exempt type, clear ledger
      if (name === 'transaction_type' && LEDGER_EXEMPT.includes(value)) {
        updated.ledger_id = '';
        updated.to_ledger_id = '';
      }

      // If switching to self_transfer, clear vendor and category (transfers don't use them)
      if (name === 'transaction_type' && value === SELF_TRANSFER_TYPE) {
        updated.vendor_id = '';
        updated.category_id = '';
      }

      // If switching away from self_transfer, clear to_ledger_id
      if (name === 'transaction_type' && value !== SELF_TRANSFER_TYPE) {
        updated.to_ledger_id = '';
      }

      // If switching away from payable, clear commitment_date
      if (name === 'transaction_type' && value !== 'payable') {
        updated.commitment_date = '';
      }

      // If category changes and is NOT a Vendor Payment category, clear vendor_id
      if (name === 'category_id') {
        const selectedCat = categories.find(c => c.id === value);
        if (!isVendorPaymentCategory(selectedCat)) {
          updated.vendor_id = '';
        }
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const txnType = formData.transaction_type;
    const needsLedger = BALANCE_AFFECTING.includes(txnType);
    const isExempt = LEDGER_EXEMPT.includes(txnType);
    const isTransfer = txnType === SELF_TRANSFER_TYPE;

    // Self-transfer validation
    if (isTransfer) {
      if (!formData.ledger_id || !formData.to_ledger_id) {
        toast.error('Please select both source and destination ledgers.');
        return;
      }
      if (formData.ledger_id === formData.to_ledger_id) {
        toast.error('Source and destination ledgers must be different.');
        return;
      }
    }

    // Ledger validation: only require for balance-affecting types (not transfers)
    if (needsLedger && !isTransfer && !formData.ledger_id) {
      toast.error('Ledger is required for this transaction type.');
      return;
    }

    // ENTERPRISE RULE: For payable/receivable WITHOUT a vendor selected,
    // category and description are mandatory (system vendor will be auto-assigned)
    const isPayableOrReceivable = txnType === 'payable' || txnType === 'receivable';
    const hasVendorSelected = !!formData.vendor_id;
    if (isPayableOrReceivable && !hasVendorSelected) {
      if (!formData.category_id) {
        toast.error('Category is required for payable/receivable entries without a vendor.');
        return;
      }
      if (!formData.description?.trim() && !formData.notes?.trim()) {
        toast.error('Description is required for payable/receivable entries without a vendor.');
        return;
      }
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }

    setSubmitting(true);
    try {
      // Determine if this is a Vendor Payment
      const selectedCategory = categories.find(c => c.id === formData.category_id);
      const isVendorPayment = isVendorPaymentCategory(selectedCategory) && formData.vendor_id;

      // For Vendor Payment with vendor selected: use the vendor entry API (single source of truth)
      if (isVendorPayment && !isTransfer) {
        // Map transaction types to vendor entry types
        // payable/receivable pass through directly, expense_paid → expense, income_received → income
        let entryType;
        if (txnType === 'payable' || txnType === 'receivable') {
          entryType = txnType; // Pass through for non-cash obligations
        } else {
          entryType = isIncomeType(txnType) ? 'income' : 'expense';
        }
        await vendorAPI.createEntry(formData.vendor_id, {
          entry_type: entryType,
          amount: parseFloat(formData.amount),
          description: formData.description,
          category_id: formData.category_id || undefined,
          // For payable/receivable, don't send ledger_id - backend uses system ledger
          ledger_id: (txnType === 'payable' || txnType === 'receivable') ? undefined : (formData.ledger_id || undefined),
          entry_date: formData.transaction_date,
        });
        const entryLabel = txnType === 'payable' ? 'payable' : txnType === 'receivable' ? 'receivable' : entryType;
        toast.success(`Vendor ${entryLabel} recorded`);
      } else {
        // Regular transaction creation
        // For self_transfer, explicitly exclude category_id (empty string fails UUID validation)
        const payload = {
          transaction_type: formData.transaction_type,
          amount: parseFloat(formData.amount),
          description: formData.description || undefined,
          transaction_date: formData.transaction_date || undefined,
          notes: formData.notes || undefined,
          vendor_id: formData.vendor_id || undefined,
          ledger_id: isExempt ? undefined : (formData.ledger_id || undefined),
          to_ledger_id: isTransfer ? formData.to_ledger_id : undefined,
          commitment_date: formData.commitment_date || undefined,
          // Only include category_id for non-transfer types
          category_id: isTransfer ? undefined : (formData.category_id || undefined),
        };
        await transactionAPI.create(payload);
        toast.success(isTransfer ? 'Transfer completed' : 'Transaction added');
      }

      resetForm();
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add transaction');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_type: 'expense_paid',
      ledger_id: ledgers[0]?.id || '',
      to_ledger_id: '',
      category_id: '',
      vendor_id: '',
      amount: '',
      description: '',
      transaction_date: new Date().toISOString().split('T')[0],
      commitment_date: '',
      notes: '',
    });
  };

  if (!isOpen) return null;

  // ENTERPRISE RULE: Filter out system categories like "Internal Transfer" from user selection
  // Internal Transfer is system-only, created automatically by transfer flow
  const filteredCategories = categories.filter(c => {
    // Exclude "Internal Transfer" system category - it's for system use only
    if (c.is_system && c.name === 'Internal Transfer') return false;
    if (isIncomeType(formData.transaction_type)) return c.category_type === 'income';
    return c.category_type === 'expense';
  }).sort((a, b) => {
    if (a.is_bookmarked && !b.is_bookmarked) return -1;
    if (!a.is_bookmarked && b.is_bookmarked) return 1;
    if (a.is_bookmarked && b.is_bookmarked) return (a.bookmark_order || 999999) - (b.bookmark_order || 999999);
    return a.name.localeCompare(b.name);
  });

  const needsLedger = BALANCE_AFFECTING.includes(formData.transaction_type);
  const isExempt = LEDGER_EXEMPT.includes(formData.transaction_type);
  const isPayable = formData.transaction_type === 'payable';
  const isReceivable = formData.transaction_type === 'receivable';
  const isTransfer = formData.transaction_type === SELF_TRANSFER_TYPE;

  // ENTERPRISE RULE: For payable/receivable without vendor, category and description are required
  const isPayableOrReceivable = isPayable || isReceivable;
  const hasVendorSelected = !!formData.vendor_id;
  const requiresCategoryAndDesc = isPayableOrReceivable && !hasVendorSelected;

  // LEDGER BALANCE VALIDATION: Check if expense exceeds available balance
  const selectedLedger = ledgers.find(l => l.id === formData.ledger_id);
  const ledgerBalance = selectedLedger ? parseFloat(selectedLedger.balance || 0) : 0;
  const enteredAmount = parseFloat(formData.amount) || 0;
  const isExpenseType = formData.transaction_type === 'expense_paid' || formData.transaction_type === 'expense';
  const insufficientBalance = isExpenseType && enteredAmount > 0 && enteredAmount > ledgerBalance;

  // Get selected category to determine if vendor selector should show
  const selectedCategory = categories.find(c => c.id === formData.category_id);
  const showVendorSelector = isVendorPaymentCategory(selectedCategory) && vendors.length > 0 && !isTransfer;

  // When vendor is selected, lock category to prevent confusion
  const isCategoryLocked = showVendorSelector && formData.vendor_id;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-transaction-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('transactions.add')}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        {loading ? (
          <div className="modal-loading">{t('common.loading')}</div>
        ) : (
          <form className="transaction-form" onSubmit={handleSubmit}>
            {/* Transaction Type: Premium Toggle Groups */}
            <div className="form-group">
              <label>{t('transactions.type')} *</label>
              {/* Primary Types */}
              <div className="txn-type-group">
                <button
                  type="button"
                  className={`txn-pill txn-pill--expense${formData.transaction_type === 'expense_paid' ? ' txn-pill--active' : ''}`}
                  onClick={() => handleChange({ target: { name: 'transaction_type', value: 'expense_paid' } })}
                >
                  <span className="txn-pill-icon">−</span>
                  {t('transaction.expense')}
                </button>
                <button
                  type="button"
                  className={`txn-pill txn-pill--income${formData.transaction_type === 'income_received' ? ' txn-pill--active' : ''}`}
                  onClick={() => handleChange({ target: { name: 'transaction_type', value: 'income_received' } })}
                >
                  <span className="txn-pill-icon">+</span>
                  {t('transaction.income')}
                </button>
                <button
                  type="button"
                  className={`txn-pill txn-pill--transfer${formData.transaction_type === 'self_transfer' ? ' txn-pill--active' : ''}`}
                  onClick={() => handleChange({ target: { name: 'transaction_type', value: 'self_transfer' } })}
                >
                  <span className="txn-pill-icon">↔</span>
                  Transfer
                </button>
              </div>
              {/* Advanced Types — always visible */}
              <div className="txn-type-group txn-type-group--advanced">
                {TRANSACTION_TYPE_KEYS.filter(tt => ['payable', 'receivable', 'scheduled'].includes(tt.value)).map(tt => (
                  <button
                    key={tt.value}
                    type="button"
                    className={`txn-pill txn-pill--${tt.value}${formData.transaction_type === tt.value ? ' txn-pill--active' : ''}`}
                    onClick={() => handleChange({ target: { name: 'transaction_type', value: tt.value } })}
                  >
                    <span className="txn-pill-icon">{tt.icon}</span>
                    {t(tt.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>{t('transactions.amount')} ({CURRENCY.symbol}) *</label>
              <div className="amount-input-wrapper">
                <span className="currency-symbol">{CURRENCY.symbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                  className="amount-input"
                />
              </div>
            </div>

            {/* Ledger: HIDDEN for payable/receivable/scheduled (Dena Hai / Udhari) */}
            {!isExempt && (
              <div className="form-group">
                <label>{isTransfer ? t('transactions.from_ledger') : t('transactions.ledger')} {(needsLedger || isTransfer) ? '*' : `(${t('common.optional')})`}</label>
                <select name="ledger_id" value={formData.ledger_id} onChange={handleChange} required={needsLedger || isTransfer}>
                  {!needsLedger && !isTransfer && <option value="">{t('transactions.no_ledger')}</option>}
                  {isTransfer && <option value="">{t('transactions.select_source_ledger')}</option>}
                  {ledgers.map(l => (
                    <option key={l.id} value={l.id}>{l.name} (₹{parseFloat(l.balance || 0).toFixed(2)})</option>
                  ))}
                </select>
                {/* LEDGER BALANCE WARNING: Show when expense exceeds balance */}
                {insufficientBalance && (
                  <div className="txn-alert txn-alert--warning">
                    <strong>Insufficient balance.</strong><br/>
                    Available: ₹{ledgerBalance.toFixed(2)}, Required: ₹{enteredAmount.toFixed(2)}<br/>
                    <span className="txn-alert-hint">Please use <strong>Payable (Dues)</strong> instead to record without affecting balance.</span>
                  </div>
                )}
              </div>
            )}

            {/* Destination ledger: only for self_transfer */}
            {isTransfer && (
              <div className="form-group">
                <label>{t('transactions.to_ledger')} *</label>
                <select name="to_ledger_id" value={formData.to_ledger_id} onChange={handleChange} required>
                  <option value="">{t('transactions.select_dest_ledger')}</option>
                  {ledgers.filter(l => l.id !== formData.ledger_id).map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Category: HIDDEN for self_transfer (internal transfers don't use categories) */}
            {/* When vendor is selected, category is locked to prevent confusion */}
            {/* REQUIRED for payable/receivable without vendor (system vendor will be auto-assigned) */}
            {!isTransfer && (
              <div className="form-group">
                <label>
                  {t('transactions.category')} {requiresCategoryAndDesc && '*'}
                  {isCategoryLocked && <span className="txn-field-hint">(locked)</span>}
                </label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  disabled={isCategoryLocked}
                  required={requiresCategoryAndDesc}
                  className={isCategoryLocked ? 'input-locked' : ''}
                >
                  <option value="">{t('transactions.select_category')}</option>
                  {filteredCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {requiresCategoryAndDesc && !formData.category_id && (
                  <small className="txn-field-required">Required for miscellaneous payable/receivable</small>
                )}
              </div>
            )}

            {/* Vendor selector: ONLY appears when category is "Vendor Payment" */}
            {showVendorSelector && (
              <div className="form-group">
                <label>{t('transactions.vendor')} ({t('common.optional')})</label>
                <select name="vendor_id" value={formData.vendor_id} onChange={handleChange}>
                  <option value="">{t('transactions.no_vendor')}</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                <small className="txn-field-note">
                  {isIncomeType(formData.transaction_type)
                    ? 'Vendor receivable will be cleared and income recorded'
                    : 'Vendor payable will be cleared and expense recorded'}
                </small>
              </div>
            )}

            {/* Info box for payable/receivable without vendor (miscellaneous dues) */}
            {requiresCategoryAndDesc && (
              <div className="txn-alert txn-alert--info">
                <strong>Miscellaneous {isPayable ? 'Payable' : 'Receivable'}</strong><br/>
                <span className="txn-alert-hint">
                  No specific vendor? No problem! Category and description are required to track this entry properly.
                </span>
              </div>
            )}

            <div className="form-group">
              <label>{t('transactions.description')} {requiresCategoryAndDesc && '*'}</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={requiresCategoryAndDesc ? "e.g., Monthly milk bill, Office rent, etc." : "What was this for?"}
                required={requiresCategoryAndDesc}
              />
              {requiresCategoryAndDesc && !formData.description?.trim() && (
                <small className="txn-field-required">Required for miscellaneous payable/receivable</small>
              )}
            </div>

            {/* Date: For self_transfer, auto-set to today and read-only */}
            <div className="form-group">
              <label>
                {isPayable ? t('transactions.date') + ' (udhari kab li)' : t('transactions.date')}
                {isTransfer && <span className="txn-field-hint">(auto)</span>}
              </label>
              <input
                type="date"
                name="transaction_date"
                value={formData.transaction_date}
                onChange={handleChange}
                readOnly={isTransfer}
                className={isTransfer ? 'input-locked' : ''}
              />
            </div>

            {/* Commitment date: only for payable (Dena Hai / Udhari) */}
            {isPayable && (
              <div className="form-group">
                <label>{t('transactions.commitment_date')}</label>
                <input
                  type="date"
                  name="commitment_date"
                  value={formData.commitment_date}
                  onChange={handleChange}
                />
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
                {t('action.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? t('transactions.saving') : t('transactions.save')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddTransactionModal;
